/**
 * AI per taakrol — Beslissen (classificatie). Plan: docs/PLAN-LLM-ROLLEN.md, fase 1.
 *
 * Eén keuzeveld: welk endpointprofiel gesloten vragen beantwoordt (nu: de
 * videocategorie). Leeg = elke tool houdt zijn huidige model. De server
 * valideert de waarde en legt vast wie hem zette; opslaan is terug te draaien
 * met Ongedaan maken (Ctrl+Z). De testknop stuurt vaste, verzonnen invoer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { SectionHeader } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { useUserRole } from '../../../contexts/UserRoleContext';

const AI_ROLLEN_URL = `${BACKEND_URL}/Api/ai-rollen`;
const LEEG = '';

async function lees(r) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
  return d;
}

export default function AiRolBeslissen() {
  // Zetten en testen vragen admin_settings.edit (de server dwingt dat ook af);
  // met alleen view zie je de huidige stand.
  const { permissions } = useUserRole();
  const magBewerken = !!permissions?.admin_settings?.edit;
  const [status, setStatus] = useState(null);
  const [keuze, setKeuze] = useState(LEEG);
  const [bezig, setBezig] = useState(false);
  const [test, setTest] = useState(null);
  const [testBezig, setTestBezig] = useState(false);

  const laad = useCallback(async () => {
    try {
      const d = await lees(await authFetch(AI_ROLLEN_URL));
      setStatus(d);
      setKeuze(d.waarde || LEEG);
    } catch (e) {
      setStatus({ fout: e.message });
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const zet = async (profiel) => {
    const d = await lees(await authFetch(`${AI_ROLLEN_URL}/beslissen`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profiel }),
    }));
    setStatus(d);
    setKeuze(d.waarde || LEEG);
    return d;
  };

  const opslaan = async () => {
    setBezig(true);
    try {
      const d = await zet(keuze || null);
      const vorige = d.vorige || null;
      biedOngedaanAan(
        keuze ? `Beslissen gebruikt nu ${keuze}` : 'Beslissen leeggemaakt — elke tool houdt zijn huidige model',
        async () => { await zet(vorige); },
      );
    } catch (e) {
      toast.error(`Opslaan mislukt: ${e.message}`);
    } finally { setBezig(false); }
  };

  const testen = async () => {
    setTestBezig(true); setTest(null);
    try {
      setTest(await lees(await authFetch(`${AI_ROLLEN_URL}/beslissen/test`, { method: 'POST' })));
    } catch (e) {
      setTest({ fout: e.message });
    } finally { setTestBezig(false); }
  };

  if (!status) return <CircularProgress size={18} />;
  if (status.fout) {
    return <Typography variant="body2" color="error">AI-rollen laden mislukt: {status.fout}</Typography>;
  }

  const gewijzigd = (keuze || null) !== (status.waarde || null);

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader
        title="AI per taakrol"
        subtitle="Kies per soort taak welk AI-endpoint het doet. Alleen endpoints die de gegevens van die taak mogen zien, zijn te kiezen."
      />
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap', maxWidth: 720 }}>
        <TextField
          select size="small" label="Beslissen (classificatie)" value={keuze}
          onChange={(e) => setKeuze(e.target.value)} sx={{ minWidth: 280, flex: 1 }}
          disabled={!magBewerken}
          helperText={status.waarde
            ? `Ingesteld${status.gewijzigd_door ? ` door ${status.gewijzigd_door}` : ''}`
            : 'Niet ingesteld: elke tool houdt zijn huidige model'}
        >
          <MenuItem value={LEEG}>Niet ingesteld (huidig gedrag)</MenuItem>
          {(status.profielen || []).map((p) => (
            <MenuItem key={p.naam} value={p.naam} disabled={!p.sleutel_aanwezig}>
              {p.naam} — {p.model}{p.sleutel_aanwezig ? '' : ' (sleutel ontbreekt)'}
            </MenuItem>
          ))}
        </TextField>
        {magBewerken && (
          <>
            <Button size="small" variant="contained" onClick={opslaan} disabled={bezig || !gewijzigd}
              sx={{ textTransform: 'none', mt: 0.5 }}>
              {bezig ? <CircularProgress size={14} /> : 'Opslaan'}
            </Button>
            <Button size="small" variant="outlined" onClick={testen} disabled={testBezig}
              sx={{ textTransform: 'none', mt: 0.5 }}>
              {testBezig ? <CircularProgress size={14} /> : 'Test'}
            </Button>
          </>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Nu in gebruik voor: videocategorie. De test stuurt een verzonnen voorbeeld, geen echte gegevens.
      </Typography>
      {test && (
        <Typography variant="body2" sx={{ mt: 1 }} color={test.fout ? 'error' : 'text.primary'}>
          {test.fout
            ? `Test mislukt: ${test.fout}`
            : `${test.waarde} (kans ${test.kans.toFixed(2)}, ${test.bron_confidence}) — ${test.profiel} / ${test.model}, `
              + `${test.tokens_in} in / ${test.tokens_uit} uit, $${test.kosten_usd.toFixed(5)}, ${test.latency_ms} ms`}
        </Typography>
      )}
    </Box>
  );
}
